import { useMemo } from 'react';

import { getMsUntilNextSecond } from '#shared';

import type { AircraftStatus } from '#entities/types';

import { usePollingQuery, useAggregateRefetch } from '#shared/api';
import type { PollingQueryResult } from '#shared/api';

import { useGetAircraftStatusesQuery } from '../aircraftStatuses.api';

const TARGET_SECOND = 5;

const getAircraftStatusesDelay = () => getMsUntilNextSecond(TARGET_SECOND);

/**
 * `getAircraftStatusesDelay` schedules against the next occurrence of
 * `TARGET_SECOND` within a minute, so the fallback poll cycles every 60s
 * aligned to second :05. Exported so a freshness threshold can be expressed
 * in missed refreshes of THIS cadence rather than a disconnected literal.
 */
export const AIRCRAFT_POLL_MS = 60_000;

export interface AircraftStatusesPollingResult extends PollingQueryResult<
  AircraftStatus[]
> {
  /**
   * `data !== undefined` at the RTK Query cache — true even for an empty
   * roster. An empty aircraft roster is real, current data; this stays false
   * only before the FIRST payload has ever arrived.
   */
  hasData: boolean;
}

export const useQueryAircraftStatuses = (): AircraftStatusesPollingResult => {
  const queryResult = useGetAircraftStatusesQuery(undefined, {
    pollingInterval: 0,
  });

  const { getDelay, refetchSignal } = useAggregateRefetch(
    'aircraftStatuses',
    getAircraftStatusesDelay,
  );
  const pollingResult = usePollingQuery(queryResult, {
    getDelay,
    refetchSignal,
  });

  return useMemo(
    () => ({
      data: pollingResult.data?.data ?? [],
      generatedAt: pollingResult.data?.generatedAt ?? null,
      validation: null, // TODO: add validation when schema validation is added
      isLoading: pollingResult.isLoading,
      isFetching: pollingResult.isFetching,
      isError: pollingResult.isError,
      error: pollingResult.error,
      lastSentTime: pollingResult.lastSentTime,
      lastReceiveTime: pollingResult.lastReceiveTime,
      hasData: pollingResult.data !== undefined,
    }),
    [pollingResult],
  );
};
